/**
 * Employee Information Module Types
 */

// ==================== Org Chart ====================

export interface OrgChartNode {
  id: string
  name: string
  job_title: string
  department: string
  division: string
  email: string
  children: OrgChartNode[]
}

export interface OrgChartResponse {
  data: OrgChartNode[]
}

// ==================== Enums ====================

export type DocumentType =
  | 'cv'
  | 'degree'
  | 'certificate'
  | 'transcript'
  | 'passport'
  | 'ktp'
  | 'npwp'
  | 'bpjs_health'
  | 'bpjs_employment'
  | 'bank_statement'
  | 'profile_photo'
  | 'other'

export type DocumentCategory = 'identity' | 'education' | 'financial' | 'other'

export type VerificationStatus = 'pending' | 'verified' | 'rejected'

export type RelationshipType = 'spouse' | 'parent' | 'child' | 'sibling' | 'relative' | 'friend' | 'other'

export type FamilyRelationshipType = 'spouse' | 'child' | 'parent' | 'sibling' | 'other'

export type AddressType = 'current' | 'permanent' | 'mailing'

export type MaritalStatus = 'single' | 'married' | 'divorced' | 'widowed'

export type EducationLevel = 'sd' | 'smp' | 'sma' | 'd1' | 'd2' | 'd3' | 's1' | 's2' | 's3'

export type Gender = 'male' | 'female'

export type AccountType = 'savings' | 'checking' | 'payroll'

// ==================== Profile ====================

export interface EmployeeBasicInfo {
  id: string
  email: string
  full_name?: string
  department?: string
  job_title?: string
}

export interface ProfileInfo {
  id: string
  employee_id: string
  nickname?: string
  gender?: Gender
  date_of_birth?: string
  place_of_birth?: string
  nationality?: string
  religion?: string
  marital_status?: MaritalStatus
  blood_type?: string
  ktp_number?: string
  npwp_number?: string
  bpjs_health_number?: string
  bpjs_employment_number?: string
  passport_number?: string
  passport_expiry?: string
  highest_education?: EducationLevel
  education_institution?: string
  education_major?: string
  graduation_year?: number
  profile_photo_id?: string
  verification_status: VerificationStatus
  verified_by?: string
  verified_at?: string
  hr_comment?: string
  created_at: string
  updated_at: string
  employee?: EmployeeBasicInfo
}

export interface ProfileInfoInput {
  nickname?: string | null
  gender?: Gender | null
  date_of_birth?: string | null
  place_of_birth?: string | null
  nationality?: string | null
  religion?: string | null
  marital_status?: MaritalStatus | null
  blood_type?: string | null
  ktp_number?: string | null
  npwp_number?: string | null
  bpjs_health_number?: string | null
  bpjs_employment_number?: string | null
  passport_number?: string | null
  passport_expiry?: string | null
  highest_education?: EducationLevel | null
  education_institution?: string | null
  education_major?: string | null
  graduation_year?: number | null
}

// ==================== Document ====================

export interface EmployeeDocument {
  id: string
  employee_id: string
  document_type: DocumentType
  document_category: DocumentCategory
  filename: string
  original_filename: string
  file_size?: number
  mime_type?: string
  title: string
  description?: string
  document_number?: string
  issue_date?: string
  expiry_date?: string
  issuing_authority?: string
  uploaded_at: string
  verification_status: VerificationStatus
  verified_by?: string
  verified_at?: string
  rejection_reason?: string
  hr_comment?: string
  is_expired?: boolean
  days_until_expiry?: number
  created_at: string
  updated_at: string
}

export interface DocumentInput {
  document_type: DocumentType
  document_category: DocumentCategory
  title: string
  description?: string
  document_number?: string
  issue_date?: string
  expiry_date?: string
  issuing_authority?: string
}

export interface DocumentUpdateInput {
  title?: string
  description?: string
  document_number?: string
  issue_date?: string
  expiry_date?: string
  issuing_authority?: string
}

// ==================== Emergency Contact ====================

export interface EmergencyContact {
  id: string
  employee_id: string
  contact_name: string
  relationship_type: RelationshipType
  phone_primary: string
  phone_secondary?: string
  email?: string
  address?: string
  is_primary: boolean
  priority_order: number
  verification_status: VerificationStatus
  verified_by?: string
  verified_at?: string
  hr_comment?: string
  created_at: string
  updated_at: string
}

export interface EmergencyContactInput {
  contact_name: string
  relationship_type: RelationshipType
  phone_primary: string
  phone_secondary?: string
  email?: string
  address?: string
  is_primary?: boolean
  priority_order?: number
}

export interface EmergencyContactUpdateInput {
  contact_name?: string
  relationship_type?: RelationshipType
  phone_primary?: string
  phone_secondary?: string
  email?: string
  address?: string
  is_primary?: boolean
  priority_order?: number
}

// ==================== Family Member ====================

export interface FamilyMember {
  id: string
  employee_id: string
  full_name: string
  relationship_type: FamilyRelationshipType
  gender?: Gender
  date_of_birth?: string
  place_of_birth?: string
  occupation?: string
  id_number?: string
  is_dependent: boolean
  is_beneficiary: boolean
  age?: number
  verification_status: VerificationStatus
  verified_by?: string
  verified_at?: string
  hr_comment?: string
  created_at: string
  updated_at: string
}

export interface FamilyMemberInput {
  full_name: string
  relationship_type: FamilyRelationshipType
  gender?: Gender
  date_of_birth?: string
  place_of_birth?: string
  occupation?: string
  id_number?: string
  is_dependent?: boolean
  is_beneficiary?: boolean
}

export interface FamilyMemberUpdateInput {
  full_name?: string
  relationship_type?: FamilyRelationshipType
  gender?: Gender
  date_of_birth?: string
  place_of_birth?: string
  occupation?: string
  id_number?: string
  is_dependent?: boolean
  is_beneficiary?: boolean
}

// ==================== Address ====================

export interface EmployeeAddress {
  id: string
  employee_id: string
  address_type: AddressType
  street_address: string
  rt_rw?: string
  kelurahan?: string
  kecamatan?: string
  city: string
  province: string
  postal_code: string
  country: string
  is_primary: boolean
  effective_from: string
  effective_to?: string
  is_current?: boolean
  full_address?: string
  verification_status: VerificationStatus
  verified_by?: string
  verified_at?: string
  hr_comment?: string
  created_at: string
  updated_at: string
}

export interface AddressInput {
  address_type: AddressType
  street_address: string
  rt_rw?: string
  kelurahan?: string
  kecamatan?: string
  city: string
  province: string
  postal_code: string
  country?: string
  is_primary?: boolean
  effective_from?: string
}

export interface AddressUpdateInput {
  address_type?: AddressType
  street_address?: string
  rt_rw?: string
  kelurahan?: string
  kecamatan?: string
  city?: string
  province?: string
  postal_code?: string
  country?: string
  is_primary?: boolean
  effective_from?: string
  effective_to?: string
}

// ==================== Bank Account ====================

export interface BankAccount {
  id: string
  employee_id: string
  bank_name: string
  bank_code?: string
  branch_name?: string
  swift_code?: string
  account_number: string
  masked_account_number?: string
  account_holder_name: string
  account_type: AccountType
  is_primary: boolean
  supporting_document_id?: string
  verification_status: VerificationStatus
  verified_by?: string
  verified_at?: string
  hr_comment?: string
  created_at: string
  updated_at: string
}

export interface BankAccountInput {
  bank_name: string
  bank_code?: string
  branch_name?: string
  swift_code?: string
  account_number: string
  account_holder_name: string
  account_type?: AccountType
  is_primary?: boolean
  supporting_document_id?: string
}

export interface BankAccountUpdateInput {
  bank_name?: string
  bank_code?: string
  branch_name?: string
  swift_code?: string
  account_number?: string
  account_holder_name?: string
  account_type?: AccountType
  is_primary?: boolean
  supporting_document_id?: string
}

// ==================== Verification ====================

export interface VerificationAction {
  action: 'verify' | 'reject'
  comment?: string
  rejection_reason?: string
}

// ==================== Summary Types (for list views) ====================

export interface DocumentSummary {
  id: string
  document_type: DocumentType
  document_category: DocumentCategory
  title: string
  original_filename: string
  is_expired?: boolean
}

export interface EmergencyContactSummary {
  id: string
  contact_name: string
  relationship_type: RelationshipType
  phone_primary: string
  is_primary: boolean
}

export interface FamilyMemberSummary {
  id: string
  full_name: string
  relationship_type: FamilyRelationshipType
  is_dependent: boolean
  is_beneficiary: boolean
}

export interface AddressSummary {
  id: string
  address_type: AddressType
  city: string
  province: string
  is_primary: boolean
}

export interface BankAccountSummary {
  id: string
  bank_name: string
  bank_code?: string
  account_holder_name: string
  account_type: AccountType
  is_primary: boolean
  account_number?: string
}

// ==================== List Responses ====================

export interface Pagination {
  page: number
  limit: number
  total: number
  pages: number
}

export interface DocumentListResponse {
  data: DocumentSummary[]
  pagination: Pagination
}

export interface EmergencyContactListResponse {
  data: EmergencyContactSummary[]
  pagination: Pagination
}

export interface FamilyMemberListResponse {
  data: FamilyMemberSummary[]
  pagination: Pagination
}

export interface AddressListResponse {
  data: AddressSummary[]
  pagination: Pagination
}

export interface BankAccountListResponse {
  data: BankAccountSummary[]
  pagination: Pagination
}

// ==================== HR Views ====================

export interface EmployeeSummary {
  id: string
  email: string
  full_name?: string
  department?: string
  job_title?: string
}

export interface EmployeeInfoSummary {
  employee: EmployeeSummary
  profile_status: string
  documents_count: number
  documents_verified: number
  documents_pending: number
  emergency_contacts_count: number
  family_members_count: number
  addresses_count: number
  bank_accounts_count: number
  completeness_percentage: number
}

export interface EmployeeListResponse {
  data: EmployeeInfoSummary[]
  pagination: Pagination
}

export interface VerificationQueueItem {
  id: string
  employee: EmployeeSummary
  item_type: string
  item_title: string
  submitted_at: string
  document_type?: string
}

export interface VerificationQueueResponse {
  data: VerificationQueueItem[]
  pagination: Pagination
}

// ==================== Constants ====================

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  cv: 'CV / Resume',
  degree: 'University Degree',
  certificate: 'Certificate',
  transcript: 'Transcript',
  passport: 'Passport',
  ktp: 'KTP (National ID)',
  npwp: 'NPWP (Tax ID)',
  bpjs_health: 'BPJS Health',
  bpjs_employment: 'BPJS Employment',
  bank_statement: 'Bank Statement',
  profile_photo: 'Profile Photo',
  other: 'Other Document',
}

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  identity: 'Identity Documents',
  education: 'Education & Certificates',
  financial: 'Financial Documents',
  other: 'Other Documents',
}

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
}

export const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  spouse: 'Spouse',
  parent: 'Parent',
  child: 'Child',
  sibling: 'Sibling',
  relative: 'Relative',
  friend: 'Friend',
  other: 'Other',
}

export const FAMILY_RELATIONSHIP_LABELS: Record<FamilyRelationshipType, string> = {
  spouse: 'Spouse',
  child: 'Child',
  parent: 'Parent',
  sibling: 'Sibling',
  other: 'Other',
}

export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  current: 'Current Address',
  permanent: 'Permanent Address',
  mailing: 'Mailing Address',
}

export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  single: 'Single',
  married: 'Married',
  divorced: 'Divorced',
  widowed: 'Widowed',
}

export const EDUCATION_LEVEL_LABELS: Record<EducationLevel, string> = {
  sd: 'Elementary School (SD)',
  smp: 'Junior High School (SMP)',
  sma: 'Senior High School (SMA)',
  d1: 'Diploma 1 (D1)',
  d2: 'Diploma 2 (D2)',
  d3: 'Diploma 3 (D3)',
  s1: "Bachelor's Degree (S1)",
  s2: "Master's Degree (S2)",
  s3: 'Doctoral Degree (S3)',
}

export const GENDER_LABELS: Record<Gender, string> = {
  male: 'Male',
  female: 'Female',
}

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  savings: 'Savings Account',
  checking: 'Checking Account',
  payroll: 'Payroll Account',
}
